import { motion } from 'motion/react';
import { Share2 } from 'lucide-react';

interface ResultScreenProps {
  persona: string;
  onRestart: () => void;
  onProductRecommendation: () => void;
  sharer?: () => void;
}

// Persona descriptions
const personaDescriptions: Record<string, string> = {
  'livsnyderen': 'For dig handler det om at have plads til det gode liv – med ro og frihed omkring dig. Her er din anbefaling, der passer til dine behov.',
  'redebyggeren': 'Tryghed for dig og din familie betyder meget – og du vil være sikker på at være godt dækket. Her er din anbefaling, der passer til dine behov.',
  'den selvkørende': 'For dig er det vigtigt at have kontrol – især når det handler om at være godt dækket i arbejdslivet. Her er din anbefaling, der passer til dine behov. ',
  'den forberedte': 'Du trives bedst, når du har overblik – og føler dig godt dækket i arbejdslivet. Her er din anbefaling, der passer til dig og dit behov.',
  'boss i egen biks': 'Du har din egen forretning og balancerer mange forskellige arbejdsopgaver. Du ønsker sparring og løsninger, der understøtter din virksomheds udvikling',
  'førstejobberen': 'Du finder dit fodfæste på arbejdsmarkedet. Du vil gerne have vejledning og trygge rammer at udvikle dig i'
};

export function ResultScreen({ persona, onRestart, onProductRecommendation, sharer }: ResultScreenProps) {
  const confetti = Array.from({ length: 20 });
  const description = personaDescriptions[persona] || '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-bg-soft p-6 pt-12 pb-12 overflow-hidden relative"
    >
      {/* Confetti Animation */}
      {confetti.map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -20, x: Math.random() * window.innerWidth, opacity: 1 }}
          animate={{
            y: window.innerHeight + 20,
            x: Math.random() * window.innerWidth,
            rotate: Math.random() * 360,
            opacity: 0
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            delay: Math.random() * 0.5,
            ease: 'linear'
          }}
          className="absolute w-2 h-2 rounded-full"
          style={{
            backgroundColor: i % 2 === 0 ? '#D76F2C' : '#008E9F'
          }}
        />
      ))}

      <div className="max-w-md mx-auto space-y-6 relative z-10">
        {/* Persona Reveal Card */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
          className="bg-white rounded-3xl p-8 text-center space-y-6"
          style={{ boxShadow: 'var(--shadow-lift)' }}
        >
          <div className="space-y-3">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 150 }}
              className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-ase-orange/10 to-ase-petroleum/10 flex items-center justify-center overflow-hidden"
            >
              {persona === 'den selvkørende' ? (
                <img src="/assets/images/personalityTest/2c33ee0d4fc6ed90f6f41420c293e36f68052e13.png" alt="Den selvkørende" className="w-full h-full object-cover" />
              ) : persona === 'førstejobberen' ? (
                <img src="/assets/images/personalityTest/04e47bb404eff7ad41f294d79adc7e2780f2a2dd.png" alt="Førstejobberen" className="w-full h-full object-cover" />
              ) : persona === 'den forberedte' ? (
                <img src="/assets/images/personalityTest/9e3354e5824f40b2bdecf9d1b0e22375fb623282.png" alt="Den forberedte" className="w-full h-full object-cover" />
              ) : persona === 'boss i egen biks' ? (
                <img src="/assets/images/personalityTest/0fca243103754d4932d85ae3957b2cee045142e8.png" alt="Boss i egen biks" className="w-full h-full object-cover" />
              ) : persona === 'livsnyderen' ? (
                <img src="/assets/images/personalityTest/fff6657fb8013fcfdd03c1a4da72344eea2876e1.png" alt="Livsnyderen" className="w-full h-full object-cover" />
              ) : persona === 'redebyggeren' ? (
                <img src="/assets/images/personalityTest/0fca243103754d4932d85ae3957b2cee045142e8.png" alt="Redebyggeren" className="w-full h-full object-cover" />
              ) : (
                <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Happy Asbjørn variation */}
                  <circle cx="20" cy="24" r="2.5" fill="#D76F2C" />
                  <circle cx="36" cy="24" r="2.5" fill="#D76F2C" />
                  <path d="M18 32 Q28 38 38 32" stroke="#D76F2C" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                  <circle cx="28" cy="28" r="18" stroke="#D76F2C" strokeWidth="2" fill="none" />
                  <path d="M12 14 Q10 8 12 6" stroke="#D76F2C" strokeWidth="2" strokeLinecap="round" fill="none" />
                  <path d="M44 14 Q46 8 44 6" stroke="#D76F2C" strokeWidth="2" strokeLinecap="round" fill="none" />
                  <path d="M16 20 Q14 18 16 16" stroke="#008E9F" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5" />
                  <path d="M40 20 Q42 18 40 16" stroke="#008E9F" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5" />
                </svg>
              )}
            </motion.div>

            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <h2 className="text-gray-900">Du er {persona}</h2>
              {/* <p className='text-gray-600 mt-4'>Her er vores anbefaling til dig</p> */}
              <p className="text-gray-600 mt-3">{description}</p>
              {/* <p className="text-gray-600 mt-3">Vi har en anbefaling på et medlemskab, der passer perfekt til dig.</p> */}
            </motion.div>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="space-y-3"
        >
          <button
            className="btn button w-full bg-ase-orange text-white py-4 px-6 transition-all active:scale-95 personalitytest"
            style={{ boxShadow: 'var(--shadow-soft)' }}
            onClick={onProductRecommendation}
          >
            Se anbefaling
          </button>
            <a className="w-full flex items-center justify-center gap-2 text-ase-petroleum py-3 transition-all active:scale-95 personalitytest"
             onClick={() => {
              if (sharer) {
                sharer();
              }
             }
            }>
            <Share2 className="w-5 h-5" strokeWidth={1.5} />
            Del testen med venner
          </a>
          
        </motion.div>

        {/* Restart link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="text-center pt-3"
        >
          <button onClick={onRestart} className="text-ase-orange underline transition-all active:scale-95 personalitytest">
            Tag testen igen
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}
